"use client";
import React, { useActionState } from "react";
import { faUserCircle } from "@fortawesome/pro-solid-svg-icons";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

import { SubmitButton, ThemedPage } from "@/components";
import { TextInput } from "@/components/forms";
import { ButtonBar, Main, Module } from "@/components/layouts";
import { ButtonGroup } from "@/components/layouts/button-group";
import PageHeading from "@/components/page-heading";
import UserError from "@/components/user-error";
import config from "@/lib/config";

import MicrosoftSignInSVG from "../../../../public/images/ms-symbollockup_signin_light.svg";
import { handleSignInAction } from "../../../actions/submit-sign-in";

import TestSSOButton from "./test.button";

const AppleIdButton = dynamic(() => import("./appleid-button"), {
  ssr: false,
});

const GoogleButton = dynamic(() => import("./google-button"), {
  ssr: false,
});

export type SignInFormState = {
  errors: string[];
};

const initialState: SignInFormState = {
  errors: [],
};

const SignInForm = () => {
  const [state, formAction] = useActionState(handleSignInAction, initialState);
  const searchParams = useSearchParams();

  return (
    <ThemedPage>
      <Main small>
        <PageHeading>Sign-in to your account</PageHeading>
        <Module>
          <p>
            You can create a new account or sign into an existing account using
            your school, company or personal account with these providers:
          </p>
          {searchParams.get("ssoError") ? (
            <UserError
              errors={["Sign in could not be completed. Please try again"]}
            />
          ) : null}
        </Module>
        <Module className="flex justify-center flex-wrap">
          {config.features.azureSSO && (
            <span className="m-1">
              <a href="/initiate-azure-sign-in">
                <Image
                  src={MicrosoftSignInSVG}
                  style={{ display: "inline", maxWidth: "auto" }}
                  alt="Sign in with Microsoft"
                  width={215}
                  height={41}
                />
              </a>
            </span>
          )}
          {config.features.appleSSO && (
            <span className="m-1">
              <AppleIdButton />
            </span>
          )}
          {config.features.googleSSO && (
            <span className="m-1">
              <GoogleButton />
            </span>
          )}
          {config.features.testServices && (
            <span className="m-1">
              <TestSSOButton />
            </span>
          )}
        </Module>
        <form action={formAction}>
          <Module>
            <p>
              If you don&apos;t have an account, you can{" "}
              <Link href="/register" data-testid="register-link">
                create a new account
              </Link>
              . If you have forgotten your password, you can{" "}
              <Link href="/password-reset">request a password reset</Link>.
            </p>
            {state.errors.length > 0 ? (
              <UserError errors={state.errors} />
            ) : null}
            <TextInput
              name="username"
              id="username"
              className="rounded-md w-full shadow-inner p-2 border border-gray-400"
              type="text"
              maxLength={30}
              autoCapitalize="none"
              label="User name:"
            />
            <TextInput
              name="password"
              label="Password:"
              id="password"
              className="rounded-md w-full shadow-inner p-2 border border-gray-400"
              type="password"
            />
          </Module>

          <ButtonBar>
            <ButtonGroup align="right">
              <SubmitButton
                icon={faUserCircle}
                text="Sign in"
                submittingText="Signing in..."
                testId="signin-button"
              />
            </ButtonGroup>
          </ButtonBar>
        </form>
      </Main>
    </ThemedPage>
  );
};

export default SignInForm;
